import { IPerson } from '@lib/user';
import { SelectedSupplier, supplierDisplayName } from '@lib/supplier';
import { IRecord, Record, Sortable } from '@lib/util';
import { DeliveryCondition, IDeliveryConditionDto } from '@lib/delivery-conditions';
import { I18nService } from '@lib/i18n';
import { IPaymentTerm, PaymentTerm } from '@lib/payment-terms';
import { FrameworkAgreement } from '../util/demand-supplier-display/demand-supplier-framework-agreement/SupplierFrameworkAgreement';

export interface IDemandSupplierContact {
    companyId: IRecord<string>;
    companyName: string;
    contact: IPerson;
    email: string;
}

export interface IDemandSupplier {
    contact: IDemandSupplierContact;
    supplierNumber?: string | null;
    orderingNote: string;
    deliveryCondition: IDeliveryConditionDto;
    paymentTerm: IPaymentTerm;
    supplierFrameworkAgreement: FrameworkAgreement;
    paymentTermRequired: boolean;
    incotermCode?: string | null;
    defaultIncotermCode?: string | null;
}

export class DemandSupplier implements Sortable {
    constructor(
        public readonly companyId: IRecord<string>,
        public readonly companyName: string,
        public readonly contact: IPerson,
        public readonly email: string,
        public readonly orderingNote: string,
        public readonly paymentTermRequired = false,
        private _incotermCode: string | null = null,
        public readonly defaultIncotermCode: string | null = null,
        public readonly deliveryCondition?: DeliveryCondition,
        private _paymentTerm?: PaymentTerm,
        public supplierFrameworkAgreement?: FrameworkAgreement,
        public readonly supplierNumber = '',
    ) {}

    public static fromSelectedSupplier(selectedSupplier: SelectedSupplier): DemandSupplier {
        return new DemandSupplier(
            new Record(selectedSupplier.company.id),
            selectedSupplier.company.name,
            selectedSupplier.contact!.person,
            '',
            '',
            false,
            null,
            null,
            undefined,
            undefined,
            undefined,
            selectedSupplier.company.supplierNumber ?? '',
        );
    }

    public static instance(supplier: IDemandSupplier, i18n: I18nService): DemandSupplier {
        return new DemandSupplier(
            supplier.contact.companyId,
            supplier.contact.companyName,
            supplier.contact.contact,
            supplier.contact.email,
            supplier.orderingNote,
            supplier.paymentTermRequired,
            supplier.incotermCode ?? null,
            supplier.defaultIncotermCode ?? null,
            supplier.deliveryCondition ? new DeliveryCondition(supplier.deliveryCondition, i18n) : undefined,
            supplier.paymentTerm ? new PaymentTerm(supplier.paymentTerm) : undefined,
            supplier.supplierFrameworkAgreement,
            supplier.supplierNumber ?? '',
        );
    }

    public get hasDefaultContact(): boolean {
        // standard contact only when there is no contact or both first and last name are empty
        return !this.contact || (this.isBlank(this.contact.firstName) && this.isBlank(this.contact.surName));
    }

    public get displayName(): string {
        return supplierDisplayName(this.supplierNumber, this.companyName);
    }

    private isBlank(value?: string): boolean {
        return !value || value.trim().length === 0;
    }

    sortBy(): string | number {
        return this.companyName.toLowerCase();
    }

    public get paymentTerm(): PaymentTerm | undefined {
        return this._paymentTerm;
    }

    public deletePaymentTerm(): void {
        this._paymentTerm = undefined;
    }

    public updatePaymentTerm(updatedPaymentTerm: PaymentTerm): void {
        this._paymentTerm = updatedPaymentTerm;
    }

    public get valid(): boolean {
        return this.paymentTermRequired ? !!this._paymentTerm : true;
    }

    public get effectiveIncotermCode(): string | null {
        return this._incotermCode ?? this.defaultIncotermCode;
    }

    public get incotermCode(): string | null {
        return this._incotermCode;
    }

    public get hasIncotermDeviation(): boolean {
        return !!this.defaultIncotermCode && this.effectiveIncotermCode !== this.defaultIncotermCode;
    }

    public updateIncoterm(incotermCode: string | null): void {
        this._incotermCode = incotermCode;
    }
}
